import {
  UpdateCompanyRepresentative,
  AddCompanyBankData,
  UpdateCompanyBankDataOnServer,
  CleanCompany,
  AddCompanyFiscalDataOnServer,
  AddAgent,
  AddPartners,
  DeletePartners,
  UpdatePartners,
  AddDirectors,
  DeleteDirectors,
  UpdateDirectors,
  AddRelationship,
  DeleteRelationship,
  UpdateRelationship,
  UpdateRelationShipOnServer,
  AddCompanyPix,
  UpdateCompanyPixOnServer,
  DeleteCompanyEconomicGroup,
  AddCompanyInvestorLevelFile,
  AddCompanyDispensedSuitability,
  AddCompanySuitabilityFile,
  UpdateCompanyContractOnServer,
  AddCompanyContract,
  UpdateCompanyBeneficiariesOnServer,
  AddCompanyBeneficiaries
} from './../actions/company-register.action';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs/Observable';
import { AgentRegister } from '../../models/agents/agent-register';
import { CompanyData } from '../../models/agents/company-data';
import { RelationshipContact } from '../../models/agents/relationship-contact';
import { Partner } from '../../models/agents/partner';
import { Director } from '../../models/agents/director';
import { Files } from '../../models/agents/files';
import { RegistrationHistory } from '../../models/agents/registration-history';
import { UpdateCompanyOutput } from '../../models/agents/output/update-company-output';
import { RegistrationControlItem } from '../../models/enums/registration-control-item.enum';
import { RegistratinEntityType } from '../../models/enums/registration-entity-type.enum';
import { Suitability } from '../../models/suitability/suitability';
import { CompanyService } from '../../services/company.service';
import { RegistrationService } from '../../services/registration.service';
import { tap } from 'rxjs/operators';
import {
  AddCompany,
  AddCompanyAuthorizations,
  AddCompanyEconomicGroup,
  UpdateCompanyFiles,
  AddCompanyFiles,
  AddCompanyOnServer,
  AddCompanyRegistrationHistory,
  AddCompanyRepresentative,
  AddCompanySuitabilityQuestions,
  AddCompanySuitabilityScore,
  CompanyConfirmRegistration,
  DisableCompanyFile,
  DisableCompanyFileOnServer,
  InitCompanyFromServer,
  UpdateCompany,
  UpdateCompanyAuthorizationOnServer,
  UpdateCompanyEconomicGroupOnServer,
  UpdateCompanyFilesOnServer,
  UpdateCompanySuitabilityQuestionsOnServer,
  UpdateCompanySuitabilityScoreOnServer,
  DeleteCompanyRepresentative,
  AddCompanyFiscalData,
  AddCompanyPatrimonialDataOnServer
} from '../actions/company-register.action';
import { Invitation } from '../../models/agents/invitation.ts';
import { PersonType } from '../../models/enums/person-type.enum';

export class CompanyStateModel {
  serverIdentify: string | null;
  invitationIdentify: string | null;
  invitation: Invitation | null;
  document: string | null;
  company: Partial<CompanyData> | null;
  registrationHistory: Array<RegistrationHistory> | null;
  agent: Partial<AgentRegister>;
  dispensedSuitability: boolean | undefined;
}

@State<CompanyStateModel>({
  name: 'company',
  defaults: {
    company: null,
    serverIdentify: null,
    registrationHistory: null,
    invitationIdentify: null,
    document: null,
    invitation: null,
    agent: null,
    dispensedSuitability: false
  }
})
export class CompanyState {
  constructor(
    protected companyService: CompanyService,
    protected registrationService: RegistrationService,
    protected store: Store
  ) {}

  @Selector()
  static company(state: CompanyStateModel) {
    return state.company;
  }

  @Selector()
  static representatives(state: CompanyStateModel) {
    return state.company.representatives;
  }

  @Selector()
  static partners(state: CompanyStateModel) {
    return state.company.partners;
  }

  @Selector()
  static files(state: CompanyStateModel) {
    return state.company.files;
  }

  @Selector()
  static serverIdentify(state: CompanyStateModel) {
    return state.serverIdentify;
  }

  @Selector()
  static invitation(state: CompanyStateModel) {
    return state.invitation;
  }

  @Selector()
  static registrationHistory(state: CompanyStateModel) {
    return state.registrationHistory;
  }

  @Selector()
  static agent(state: CompanyStateModel) {
    return state.agent;
  }

  @Selector()
  static dispensedSuitability(state: CompanyStateModel): boolean {
    return state.dispensedSuitability;
  }

  @Action(AddCompanyRegistrationHistory)
  addCompanyRegistrationHistory(
    { setState, getState }: StateContext<CompanyStateModel>,
    { history }: AddCompanyRegistrationHistory
  ) {
    const state = getState();

    if (!state.registrationHistory) {
      state.registrationHistory = new Array<RegistrationHistory>();
    }

    if (
      state.registrationHistory.findIndex(
        (args) => args.currentStep === history.currentStep
      ) === -1
    ) {
      state.registrationHistory.push(history);
      setState({ ...state });
      return this.registrationService
        .insertNewTrack({
          agentId: state.serverIdentify,
          registrationHistory: history
        })
        .subscribe();
    }
  }

  @Action(CleanCompany)
  cleanCompany(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: AddCompany
  ) {
    patchState({ company: payload });
  }

  @Action(AddCompany)
  addCompany(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload, personType }: AddCompany
  ) {
    patchState({ company: payload });
    return this.store.dispatch(new AddCompanyOnServer(payload, personType));
  }

  @Action(AddAgent)
  addAgent(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: AddAgent
  ) {
    patchState({ agent: payload });
  }

  @Action(AddCompanyFiscalData)
  patchCompanyFiscalData(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: any
  ) {
    const company = getState().company;
    company.fiscalData = payload.fiscalData;
    company.irrf = payload.irrf;
    company.iof = payload.iof;

    patchState({ company: company });

    return this.store.dispatch(new AddCompanyFiscalDataOnServer(payload));
  }

  @Action(AddCompanyContract)
  addPersonContract(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { contractData }: AddCompanyContract
  ) {
    const company = getState().company;
    company.contractData = contractData;
    patchState({ company });
    return this.store.dispatch(new UpdateCompanyContractOnServer(contractData));
  }

  @Action(UpdateCompanyContractOnServer)
  UpdateCompanyContractOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { contractData }: UpdateCompanyContractOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateContractData(agentId, contractData);
  }

  @Action(AddCompanyBeneficiaries)
  addCompanyBeneficiaries(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { beneficiaries }: AddCompanyBeneficiaries
  ) {
    const company = getState().company;
    if (company) {
      company.beneficiaries = beneficiaries;
      patchState({ company });
    }

    return this.store.dispatch(
      new UpdateCompanyBeneficiariesOnServer(beneficiaries)
    );
  }

  @Action(UpdateCompanyBeneficiariesOnServer)
  updatePersonBeneficiariesOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { beneficiaries }: UpdateCompanyBeneficiariesOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateBeneficiariesData(
      agentId,
      beneficiaries
    );
  }

  @Action(AddCompanyFiscalDataOnServer)
  patchCompanyFiscalDataOnServer(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: any
  ) {
    return this.companyService.patchFiscalData(
      payload.irrf,
      payload.iof,
      payload.fiscalData,
      payload.id
    );
  }

  @Action(UpdateCompany)
  updateCompany(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { company }: UpdateCompany
  ): Observable<UpdateCompanyOutput> {
    const currentCompany = getState().company;
    const agentId = getState().serverIdentify;
    const companyUpdated: CompanyData = { ...currentCompany, ...company };
    patchState({
      company: companyUpdated
    });

    return this.companyService.updateCompany(agentId, companyUpdated);
  }

  @Action(CompanyConfirmRegistration)
  companyConfirmRegistration(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { confirmed }: CompanyConfirmRegistration
  ) {
    const currentState = getState();
    const agentId = getState().serverIdentify;

    currentState.company.confirmed = confirmed;
    patchState({ ...currentState });

    return this.companyService.confirmRegistration(agentId);
  }

  @Action(AddCompanyOnServer)
  createAgent(
    { patchState }: StateContext<CompanyStateModel>,
    { payload, personType }: AddCompanyOnServer
  ) {
    const agentRegister = this.createAgentPayload(payload, personType);
    const companyObservable = new Observable((observer) => {
      this.registrationService.registerAgent(agentRegister).subscribe((res) => {
        patchState({
          serverIdentify: res.agent.id,
          registrationHistory: res.agent.registrationHistory
        });
        this.companyService
          .registerCompany({
            agentId: res.agent.id,
            company: payload,
            userId: ''
          })
          .subscribe(
            (response) => {
              observer.next(response.company);
              observer.complete();
            },
            (error) => {
              observer.error(error);
            }
          );
      });
    });
    return companyObservable;
  }

  @Action(AddCompanyRepresentative)
  addCompanyRepresentative(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: AddCompanyRepresentative
  ) {
    const company = getState().company;
    company.representatives = company.representatives || [];

    patchState({
      company: {
        ...company,
        representatives: [...company.representatives, ...payload]
      }
    });
  }

  @Action(DeleteCompanyRepresentative)
  deleteCompanyRepresentative(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: DeleteCompanyRepresentative
  ) {
    const company = getState().company;
    patchState({
      company: {
        ...company,
        representatives: company.representatives.filter(
          (representative) => representative.id !== payload.id
        )
      }
    });
  }

  @Action(UpdateCompanyRepresentative)
  UpdateCompanyRepresentative(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: UpdateCompanyRepresentative
  ) {
    const company = getState().company;

    company.representatives = company.representatives.map((representative) =>
      representative.id === payload.id ? payload : representative
    );

    patchState({
      company: {
        ...company,
        representatives: company.representatives
      }
    });
  }

  @Action(AddCompanyBankData)
  addCompanyBankData(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { bankData }: AddCompanyBankData
  ) {
    const company = getState().company;
    company.bankData = bankData;
    patchState({ company });
    return this.store.dispatch(new UpdateCompanyBankDataOnServer(bankData));
  }

  @Action(AddCompanyFiles)
  addCompanyFiles(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { files, updateOnServer }: AddCompanyFiles
  ) {
    const company = getState().company;
    let allFiles = Array.isArray(company.files)
      ? [...company.files, ...files]
      : files;
    allFiles = allFiles.filter(
      (v, i, a) => a.findIndex((t) => t.fileName === v.fileName) === i
    );
    patchState({ company: { ...company, files: allFiles } });
    if (updateOnServer) {
      return this.store.dispatch(new UpdateCompanyFilesOnServer(allFiles));
    }
  }

  @Action(UpdateCompanyFiles)
  updateCompanyFiles(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { files }: UpdateCompanyFiles
  ) {
    patchState({ company: { files } });

    return this.store.dispatch(new UpdateCompanyFilesOnServer(files));
  }

  @Action(DisableCompanyFile)
  disableCompanyFile(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { file }: DisableCompanyFile
  ) {
    const company = getState().company;

    if (!company) {
      return;
    }

    const companyFiles = company.files.map((companyFile) => {
      if (companyFile.fileName === file.fileName) {
        companyFile.isDeleted = true;
      }

      return companyFile;
    });

    if (!company.files || company.files.length) {
      company.files = new Array<Files>();
    }

    company.files = companyFiles;
    patchState({ company });
    return this.store.dispatch(new DisableCompanyFileOnServer(file.fileUrl));
  }

  @Action(DisableCompanyFileOnServer)
  disableCompanyFileOnServer(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { fileUrl }: DisableCompanyFileOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.disableCompanyFile(agentId, fileUrl);
  }

  @Action(UpdateCompanyBankDataOnServer)
  updateCompanyBankDataOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { bankData }: UpdateCompanyBankDataOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateBankData(agentId, bankData);
  }

  @Action(AddCompanyEconomicGroup)
  addCompanyEconomicGroup(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: AddCompanyEconomicGroup
  ) {
    const company = getState().company;
    company.economicGroup = payload;
    patchState({ company });
    return this.store.dispatch(new UpdateCompanyEconomicGroupOnServer(payload));
  }

  @Action(DeleteCompanyEconomicGroup)
  deleteCompanyEconomicGroup(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { index }: DeleteCompanyEconomicGroup
  ) {
    const company = getState().company;
    company.economicGroup.groups.splice(index, 1);
    patchState({ company: company });
  }

  @Action(AddCompanyAuthorizations)
  addCompanyAuthorizations(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: AddCompanyAuthorizations
  ) {
    const company = getState().company;
    company.authorizations = payload;
    patchState({ company });
    return this.store.dispatch(new UpdateCompanyAuthorizationOnServer(payload));
  }

  @Action(AddCompanySuitabilityQuestions)
  addCompanySuitabilityQuestions(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { payload }: AddCompanySuitabilityQuestions
  ) {
    const company = getState().company;

    if (!company.suitability) {
      company.suitability = new Suitability();
    }

    company.suitability = payload;
    patchState({ company });
    return this.store.dispatch(
      new UpdateCompanySuitabilityQuestionsOnServer(payload)
    );
  }

  @Action(AddCompanySuitabilityScore)
  addCompanySuitabilityScore(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { suitabilityScore }: AddCompanySuitabilityScore
  ) {
    const company = getState().company;

    if (!company.suitability) {
      company.suitability = new Suitability();
    }

    company.suitability.profile = suitabilityScore.profile;
    company.suitability.score = suitabilityScore.score;

    patchState({ company: company });
    return this.store.dispatch(
      new UpdateCompanySuitabilityScoreOnServer(suitabilityScore)
    );
  }

  @Action(UpdateCompanyEconomicGroupOnServer)
  updateCompanyEconomicGroupOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { payload }: UpdateCompanyEconomicGroupOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateEconomicGroup(agentId, payload);
  }

  @Action(UpdateCompanyFilesOnServer)
  updateCompanyFilesOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { files }: UpdateCompanyFilesOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateFiles(agentId, files);
  }

  @Action(UpdateCompanyAuthorizationOnServer)
  updateCompanyAuthorizationOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { payload }: UpdateCompanyAuthorizationOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateAuthorizations(agentId, payload);
  }

  @Action(UpdateCompanySuitabilityQuestionsOnServer)
  updateCompanySuitabilityQuestionsOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { payload }: UpdateCompanySuitabilityQuestionsOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateSuitabilityQuestions(agentId, payload);
  }

  @Action(UpdateCompanySuitabilityScoreOnServer)
  updateCompanySuitabilityScoreOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { suitabilityScore }: UpdateCompanySuitabilityScoreOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updateSuitabilityScore(
      agentId,
      suitabilityScore
    );
  }

  @Action(InitCompanyFromServer)
  initCompanyFromServer(
    { patchState }: StateContext<CompanyStateModel>,
    {
      company,
      registrationHistory,
      serverIdentify,
      invitation
    }: InitCompanyFromServer
  ) {
    patchState({ company, serverIdentify, registrationHistory, invitation });
  }

  @Action(AddPartners)
  addPartners(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { partner }: AddPartners
  ) {
    const company = getState().company;
    if (!company.partners) {
      company.partners = new Array<Partner>();
    }
    company.partners.push(partner);
    patchState({ company: company });
  }

  @Action(AddDirectors)
  addDirects(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { director }: AddDirectors
  ) {
    const company = getState().company;
    if (!company.directors) {
      company.directors = new Array<Director>();
    }
    company.directors.push(director);
    patchState({ company: company });
  }

  @Action(DeletePartners)
  deletePartners(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { index }: DeletePartners
  ) {
    const company = getState().company;
    company.partners.splice(index, 1);
    patchState({ company: company });
  }

  @Action(DeleteDirectors)
  deleteDirectors(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { index }: DeleteDirectors
  ) {
    const company = getState().company;
    company.directors.splice(index, 1);
    patchState({ company: company });
  }

  @Action(UpdatePartners)
  updatePartners(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { partner, index }: UpdatePartners
  ) {
    const company = getState().company;
    company.partners[index] = partner;
    patchState({ company: company });
  }

  @Action(AddCompanyDispensedSuitability)
  updateCompanyDispesedSuitabilityStore(
    { patchState }: StateContext<CompanyStateModel>,
    { dispensedSuitability }: AddCompanyDispensedSuitability
  ) {
    patchState({ dispensedSuitability });
  }

  @Action(UpdateRelationship)
  updateRelationship(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { contact, index }: UpdateRelationship
  ) {
    const company = getState().company;
    const agentId = getState().serverIdentify;
    company.relationshipContacts[index] = contact;
    patchState({ company: company });
    this.store.dispatch(
      new UpdateRelationShipOnServer(agentId, company.relationshipContacts)
    );
  }

  @Action(UpdateDirectors)
  updateDirectors(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { director, index }: UpdateDirectors
  ) {
    const company = getState().company;
    const agentId = getState().serverIdentify;

    company.directors[index] = director;

    patchState({ company: company });
    this.store.dispatch(
      new UpdateRelationShipOnServer(agentId, company.relationshipContacts)
    );
  }

  @Action(DeleteRelationship)
  deleteRelationship(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { index }: DeleteRelationship
  ) {
    const company = getState().company;
    const agentId = getState().serverIdentify;

    company.relationshipContacts.splice(index, 1);

    patchState({ company: company });
    this.store.dispatch(
      new UpdateRelationShipOnServer(agentId, company.relationshipContacts)
    );
  }

  @Action(AddRelationship)
  addRelationship(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { contact }: AddRelationship
  ) {
    const company = getState().company;
    const agentId = getState().serverIdentify;
    if (!company.relationshipContacts) {
      company.relationshipContacts = new Array<RelationshipContact>();
    }
    company.relationshipContacts.push(contact);
    patchState({ company: company });
    this.store.dispatch(
      new UpdateRelationShipOnServer(agentId, company.relationshipContacts)
    );
  }

  @Action(UpdateRelationShipOnServer)
  updateRelationshipOnServer(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { agentId, contacts }: UpdateRelationShipOnServer
  ) {
    return this.companyService.patchRelationshipContacts(agentId, contacts);
  }

  @Action(AddCompanyPix)
  addCompanyPix(
    { getState, patchState }: StateContext<CompanyStateModel>,
    { pixs }: AddCompanyPix
  ) {
    const company = getState().company;
    company.pixs = pixs;
    patchState({ company });
    return this.store.dispatch(new UpdateCompanyPixOnServer(pixs));
  }

  @Action(UpdateCompanyPixOnServer)
  updateCompanyPixOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { pixs }: UpdateCompanyPixOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updatePixData(agentId, pixs);
  }

  @Action(AddCompanyPatrimonialDataOnServer)
  updateCompanyPatrimonialOnServer(
    { getState }: StateContext<CompanyStateModel>,
    { payload }: AddCompanyPatrimonialDataOnServer
  ) {
    const agentId = getState().serverIdentify;
    return this.companyService.updatePatrimonial(agentId, payload);
  }

  @Action(AddCompanySuitabilityFile)
  addCompanySuitabilityFile(
    { patchState, getState }: StateContext<CompanyStateModel>,
    { virtualRawFingerprint }: AddCompanySuitabilityFile
  ) {
    const company = getState().company;
    const agentId = getState().serverIdentify;
    return this.companyService
      .generateSuitabilityFile(agentId, virtualRawFingerprint)
      .pipe(
        tap((files: Array<Files>) => {
          company.files = files;
          patchState({ company });
        })
      );
  }

  @Action(AddCompanyInvestorLevelFile)
  addCompanyInvestorLevelFile(
    { getState, patchState }: StateContext<CompanyStateModel>,
  ) {
    const company = getState().company;
    const agentId = getState().serverIdentify;
    return this.companyService.generateInvestorLevelFile(agentId).pipe(
      tap((files: Array<Files>) => {
        company.files = files;
        patchState({ company });
      })
    );
  }

  createAgentPayload(company: CompanyData, type: PersonType): AgentRegister {
    return new AgentRegister({
      id: null,
      document: company.socialNumber,
      inviteID: company.inviteID,
      personType: type,
      entitiesType: this.createEntityType(),
      status: 'Em preenchimento',
      registrationHistory: this.createInitalRegistrationHistory(),
      pep: false,
      cft: false,
      cftVerificationDate: null,
      investorRepresentative: null,
      email: null,
      cellPhone: null,
      HandShake: null,
      isExternal: null,
      isRepresentativeFilling: null,
      madeByRepresentative: null,
      updateProcess: false,
      requestedLastUpdateAt: null,
      distributor: company.distributor
    });
  }

  createEntityType(): string[] {
    return [RegistratinEntityType.Cotista];
  }

  createInitalRegistrationHistory(): RegistrationHistory[] {
    return [
      this.registrationService.getRegistrationItem(
        RegistrationControlItem.InvestorData
      ),
      this.registrationService.getRegistrationItem(
        RegistrationControlItem.Representatives
      )
    ];
  }
}
